import { motion } from 'framer-motion';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, User, Clock } from 'lucide-react';

const blogPosts = [
  {
    id: 1,
    title: 'Top 10 Trends Shaping Trade Fairs in 2025',
    excerpt: 'Discover the latest innovations and strategies that are transforming the exhibition industry worldwide.',
    image: 'https://images.unsplash.com/photo-1740727263760-d220d5a90d9f?crop=entropy&cs=srgb&fm=jpg&q=85',
    category: 'Industry Trends',
    author: 'DFAIRS Team',
    date: 'January 15, 2025',
    readTime: '5 min read'
  },
  {
    id: 2,
    title: 'How to Maximize ROI from Your Exhibition Booth',
    excerpt: 'Expert tips and strategies to ensure your trade fair investment delivers measurable business results.',
    image: 'https://images.unsplash.com/photo-1761195689615-9469b65dac01?crop=entropy&cs=srgb&fm=jpg&q=85',
    category: 'Tips & Guides',
    author: 'DFAIRS Team',
    date: 'January 10, 2025',
    readTime: '7 min read'
  },
  {
    id: 3,
    title: 'The Rise of Hybrid Events: Blending Physical & Digital',
    excerpt: 'Understanding the new event landscape and how to create seamless hybrid experiences.',
    image: 'https://images.unsplash.com/photo-1505373877841-8d25f7d46678?crop=entropy&cs=srgb&fm=jpg&q=85',
    category: 'Event Technology',
    author: 'DFAIRS Team',
    date: 'January 5, 2025',
    readTime: '6 min read'
  },
  {
    id: 4,
    title: 'Brand Activation Success Stories: Lessons Learned',
    excerpt: 'Case studies from successful brand activation campaigns and what made them work.',
    image: 'https://images.pexels.com/photos/29708240/pexels-photo-29708240.jpeg',
    category: 'Case Studies',
    author: 'DFAIRS Team',
    date: 'December 28, 2024',
    readTime: '8 min read'
  },
  {
    id: 5,
    title: 'Sustainable Event Management: A Complete Guide',
    excerpt: 'How to reduce environmental impact while delivering exceptional event experiences.',
    image: 'https://images.unsplash.com/photo-1768396855390-0728fa9c21e1?crop=entropy&cs=srgb&fm=jpg&q=85',
    category: 'Sustainability',
    author: 'DFAIRS Team',
    date: 'December 20, 2024',
    readTime: '6 min read'
  },
  {
    id: 6,
    title: 'Choosing the Right Venue for Your Corporate Event',
    excerpt: 'Key factors to consider when selecting the perfect venue for your next corporate gathering.',
    image: 'https://images.unsplash.com/photo-1727931287903-b24dd8011a56?crop=entropy&cs=srgb&fm=jpg&q=85',
    category: 'Planning',
    author: 'DFAIRS Team',
    date: 'December 15, 2024',
    readTime: '5 min read'
  }
];

const categories = ['All', 'Industry Trends', 'Tips & Guides', 'Event Technology', 'Case Studies', 'Sustainability', 'Planning'];

export default function BlogPage() {
  return (
    <div className="page-transition" data-testid="blog-page">
      <Navbar />
      <main>
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-24 bg-navy overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-3xl"
            >
              <span className="text-gold font-semibold uppercase tracking-wider text-sm mb-4 block">
                Insights & Resources
              </span>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-white mb-6">
                Industry <span className="text-gold">Insights</span>
              </h1>
              <p className="text-white/80 text-lg md:text-xl leading-relaxed">
                Stay updated with the latest trends, tips, and insights from the world of exhibitions and events.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Categories */}
        <section className="py-8 bg-white border-b border-gray-100 sticky top-[72px] z-30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-wrap gap-3 justify-center">
              {categories.map((category) => (
                <button
                  key={category}
                  className={`px-5 py-2 text-sm font-medium transition-colors ${
                    category === 'All'
                      ? 'bg-navy text-white'
                      : 'bg-muted text-navy hover:bg-navy/10'
                  }`}
                  data-testid={`category-${category.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Blog Grid */}
        <section className="py-16 lg:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {blogPosts.map((post, index) => (
                <motion.article
                  key={post.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="blog-card bg-white border border-gray-100"
                  data-testid={`blog-post-${post.id}`}
                >
                  <Link to={`/blog/${post.id}`} className="block">
                    <div className="h-52 overflow-hidden">
                      <img
                        src={post.image}
                        alt={post.title}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-6">
                      <span className="text-gold text-sm font-medium uppercase tracking-wider">
                        {post.category}
                      </span>
                      <h2 className="font-heading text-xl text-navy mt-2 mb-3 line-clamp-2 hover:text-gold transition-colors">
                        {post.title}
                      </h2>
                      <p className="text-muted-foreground text-sm mb-4 line-clamp-2">
                        {post.excerpt}
                      </p>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {post.author}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {post.date}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {post.readTime}
                        </span>
                      </div>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>

            {/* Load More */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mt-12"
            >
              <button className="btn-secondary inline-flex items-center gap-2">
                Load More Articles
                <ArrowRight className="w-4 h-4" />
              </button>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
